import { getLogger } from '@logtape/logtape';
import { AxiosResponse } from 'axios';

import { ApiError, api } from '@/lib/api-client.ts';

import {
    FetchByInstanceResult,
    FuseResultMatch,
    SearchDataCurPatientResponse,
    SearchDocCirrusPatientResponse,
    SearchResult,
} from '@/models/Fuse.ts';
import { GenericPatient } from '@/models/GenericPatient.ts';
import { Patient, PatientFormData, UpdatePatient } from '@/models/Patient.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { DataCurUser } from '@/models/data-cur/DataCurUser.ts';

import {
    SchedulerInsuranceInfoSchema,
    ValidateUserInsuranceResponse,
    validateUserInsuranceResponseSchema,
} from '@/schemas/validate-insurance-response.schema.ts';

import { getPVSServiceStrategy } from '@/services/pvsStrategy/strategySelector.ts';

import { getInstanceConfig } from '@/utils/configUtils.ts';
import { getCustomerId } from '@/utils/customer-utils.ts';
import { getPatientSearchQueries } from '@/utils/date-utils.ts';
import { serializeApiError } from '@/utils/log-utils.ts';
import { convertUserToGenericPatient, getPatientIdFromUser, removeEmptyPatientFields } from '@/utils/patientUtils.ts';

import { ServiceUrl } from './ServiceURL.ts';

const logger = getLogger(['service', 'patient']);

interface PatientApiResponse<
    T extends
        | DataCurUser
        | Patient
        | SearchDataCurPatientResponse
        | SearchDocCirrusPatientResponse
        | ValidateUserInsuranceResponse,
> {
    data: T;
}

export const getUserById = async <T extends GenericPatient | Patient>(
    user_id: string,
    instance_id: string,
    pvs_provider: PVSProvider,
    keep_dc_user_object?: boolean
): Promise<T | undefined> => {
    try {
        const { api_url, params } = getPVSServiceStrategy(pvs_provider).createUserFetchRequest(user_id, instance_id);
        const response: AxiosResponse<PatientApiResponse<DataCurUser | Patient>> = await api.get(api_url, {
            headers: {
                customer_id: getCustomerId(),
            },
            params: params,
        });

        const user = response.data?.data;
        if (!user) return undefined;

        if (pvs_provider === PVSProvider.DOC_CIRRUS && keep_dc_user_object) return user as T;

        return convertUserToGenericPatient(user, pvs_provider) as T;
    } catch (error) {
        logger.debug('Failed to fetch the user by id', { error: error, ...serializeApiError(error) });
        return undefined;
    }
};

export const createUser = async (
    patient_data: PatientFormData,
    pvs_provider: PVSProvider,
    is_data_admission_to_pvs_approved: boolean
): Promise<GenericPatient | undefined> => {
    const remove_empty_values = removeEmptyPatientFields(patient_data);
    try {
        const { api_url, params, body } = getPVSServiceStrategy(pvs_provider).createUserCreateRequest(
            remove_empty_values,
            is_data_admission_to_pvs_approved
        );
        const response: AxiosResponse<PatientApiResponse<DataCurUser | Patient>> = await api.post(api_url, body, {
            params: params,
            headers: {
                customer_id: getCustomerId(),
            },
        });

        const user = response.data?.data;
        if (!user) return undefined;

        return convertUserToGenericPatient(user, pvs_provider);
    } catch (error) {
        logger.debug('Failed to create the user', { error: error, ...serializeApiError(error) });
        throw error;
    }
};

export const updateUser = async (
    user_id: string,
    instance_id: string,
    update_data: UpdatePatient,
    pvs_provider: PVSProvider
): Promise<GenericPatient | undefined> => {
    if (pvs_provider !== PVSProvider.DOC_CIRRUS) {
        // BE doesn't support update user for data-cur
        logger.error('Update user is not supported for {pvsProvider} PVS provider', { pvsProvider: pvs_provider });
        return undefined;
    }

    try {
        const { api_url, params, body } = getPVSServiceStrategy(pvs_provider).createUserUpdateRequest(
            user_id,
            instance_id,
            update_data
        );
        const response: AxiosResponse<PatientApiResponse<DataCurUser | Patient>> = await api.post(api_url, body, {
            headers: {
                customer_id: getCustomerId(),
            },
            params: params,
        });

        const user = response.data?.data;
        if (!user) return undefined;

        return convertUserToGenericPatient(user, pvs_provider);
    } catch (error) {
        logger.debug('Error updating user', { error: error, ...serializeApiError(error) });
        return undefined;
    }
};

const searchUserOnSingleInstance = async (
    instance_id: string,
    query: string
): Promise<FetchByInstanceResult | undefined> => {
    // fetch pvs provider from instance config
    const pvs_provider = getInstanceConfig(instance_id)?.pvs_provider;
    if (!pvs_provider) return undefined;

    let api_url: string | undefined = undefined;
    if (pvs_provider === PVSProvider.DOC_CIRRUS) {
        api_url = ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.DOC_CIRRUS.SEARCH;
    } else if (pvs_provider === PVSProvider.DATA_CUR) {
        api_url = ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.DATA_CUR.SEARCH;
    }
    if (!api_url) return undefined;

    try {
        const config = {
            headers: {
                customer_id: getCustomerId(),
            },
            params: {
                instance_id: instance_id,
                search: encodeURIComponent(query.toLowerCase().toString()),
            },
        };

        switch (pvs_provider) {
            case PVSProvider.DOC_CIRRUS: {
                const response: AxiosResponse<SearchDocCirrusPatientResponse> = await api.get(api_url, config);

                if (!response.data?.data) {
                    return undefined;
                }

                const user_search_response: SearchDocCirrusPatientResponse = response.data;

                return {
                    patients: user_search_response.data.map(
                        ({ user, matches }: { user: Patient; matches: FuseResultMatch[] }) => ({
                            user: {
                                ...user,
                                _normalized: {
                                    patient_number: user.account.patient_number?.toLowerCase().trim() ?? '',
                                    primary_email: user.primary_email?.toLowerCase().trim() ?? '',
                                    first_name: user.name.first_name.toLowerCase().trim(),
                                    last_name: user.name.last_name.toLowerCase().trim(),
                                    date_of_birth: user.account.date_of_birth.toLowerCase().trim(),
                                },
                            },
                            matches: matches,
                        })
                    ),
                    results_count: user_search_response.meta.results_count,
                    total_count: user_search_response.meta.total_count,
                };
            }
            case PVSProvider.DATA_CUR: {
                const response: AxiosResponse<SearchDataCurPatientResponse> = await api.get(api_url, config);

                if (!response.data?.data) {
                    return undefined;
                }

                const user_search_response: SearchDataCurPatientResponse = response.data;

                return {
                    patients: user_search_response.data.map((user) => ({
                        user: user,
                        matches: [],
                    })),
                    results_count: user_search_response.meta.count,
                    total_count: user_search_response.meta.count,
                };
            }
            default: {
                return undefined;
            }
        }
    } catch {
        return undefined;
    }
};

export const searchUserOnAllInstances = async (instance_ids: string[], query: string): Promise<SearchResult> => {
    const startTime = performance.now();
    const searchQueries = getPatientSearchQueries(query);
    const promises = instance_ids.flatMap((instance_id) =>
        searchQueries.map((searchQuery) => searchUserOnSingleInstance(instance_id, searchQuery))
    );
    const responses: (FetchByInstanceResult | undefined)[] = await Promise.all(promises);
    const filteredResponses: FetchByInstanceResult[] = responses.filter((response) => !!response);
    const seenPatientKeys = new Set<string>();
    const patients = filteredResponses
        .flatMap((response) => response.patients)
        .filter((patient) => {
            const patientId = getPatientIdFromUser(patient.user);
            const patientKey = patientId ? `${patient.user.instance_id}:${patientId}` : undefined;
            if (!patientKey) return true;
            if (seenPatientKeys.has(patientKey)) return false;

            seenPatientKeys.add(patientKey);
            return true;
        });
    const isExpandedSearch = searchQueries.length > 1;
    const endTime = performance.now();
    return {
        patients: patients,
        results_count: isExpandedSearch
            ? patients.length
            : filteredResponses.reduce((acc, response) => acc + response.results_count, 0),
        total_count: isExpandedSearch
            ? patients.length
            : filteredResponses.reduce((acc, response) => acc + response.total_count, 0),
        duration: endTime - startTime,
    };
};

interface ValidateInsuranceBody {
    schedule_insurance_info: SchedulerInsuranceInfoSchema[];
    allow_duplication: boolean;
}

// TODO: check if data-cur implementation is needed
export const validateInsuranceUser = async (
    customer_id: string,
    instance_id: string,
    user_id: string, // dc_user_id or eterno_user_id
    data: ValidateInsuranceBody
): Promise<ValidateUserInsuranceResponse> => {
    if (!customer_id || !instance_id || !user_id) {
        throw new ApiError(400, 'missing customer id or instance id or user id');
    }

    const response: AxiosResponse<PatientApiResponse<ValidateUserInsuranceResponse>> = await api.post(
        `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.DOC_CIRRUS.USERS}/validate-insurance`,
        data,
        {
            headers: {
                customer_id: customer_id,
            },
            params: {
                instance_id: instance_id,
                user_id: user_id,
            },
        }
    );

    const parsed = validateUserInsuranceResponseSchema.safeParse(response.data?.data);
    if (!parsed.success) {
        logger.warn('validateInsuranceUser: unexpected response shape, {issueCount} issue(s)', {
            issueCount: parsed.error.issues.length,
        });
        throw new ApiError(502, 'Invalid validateInsuranceUser response');
    }
    return parsed.data;
};
